// Quelle FORME d'échelles un graphique demande — D-8 (plan 12/09), revue codex #260.
//
// Extrait de ChartBlock parce que le choix de branche y était faux et que rien ne pouvait
// le dire. Le bloc `scales` traitait les barres horizontales dans la seule branche
// `empile && horizontal` ; un `bar` horizontal NON empilé retombait dans la branche par
// défaut, qui pose le formatage des VALEURS sur l'axe `y`. Or avec `indexAxis: 'y'`, `y`
// est l'axe des CATÉGORIES : Chart.js passait alors au `callback` l'INDICE du tick, et les
// libellés sortaient « 0 », « 1 », « 2 ».
//
// Personne ne l'avait vu parce que les six graphiques horizontaux d'aujourd'hui sont tous
// empilés. Les lots A-back-1 et A-back-2 en livrent quatre non empilés ce soir
// (« Puissance installée par filière », « Top 6 des sections NAF », « Grandes surfaces par
// type », « Répartition par risque / niveau d'aléa ») : le défaut serait sorti en
// production sur ces quatre-là, avec des libellés numérotés au lieu des noms de filières.
//
// La leçon, et la raison de ce fichier : l'invariant qui compte n'est pas « quelle branche »
// mais « quel axe porte les valeurs ». Il est désormais nommé, et testé.

export interface SpecEchelles {
  type: string
  horizontal?: boolean
  empile?: boolean
}

export type FormeEchelles =
  | 'anneau'             // aucune échelle
  | 'pyramide'           // miroir, x = valeurs (absolues), y = tranches d'âge
  | 'empile-horizontal'  // barres empilées 100 %, x = valeurs bornées à 100
  | 'horizontal'         // barres horizontales simples, x = valeurs, y = catégories
  | 'standard'           // vertical : y = valeurs, x = catégories

export function formeEchelles(spec: SpecEchelles): FormeEchelles {
  if (spec.type === 'doughnut') return 'anneau'
  if (spec.type === 'pyramid') return 'pyramide'
  if (spec.horizontal && spec.empile) return 'empile-horizontal'
  if (spec.horizontal) return 'horizontal'
  return 'standard'
}

/** L'axe qui porte les VALEURS — donc celui qui reçoit la grille, l'unité et le formatage
 *  des nombres. C'est l'invariant que le défaut de #260 violait : l'autre axe porte les
 *  catégories, et lui appliquer un formateur de nombres affiche des indices. */
export function axeDesValeurs(forme: FormeEchelles): 'x' | 'y' | null {
  switch (forme) {
    case 'anneau': return null
    case 'pyramide':
    case 'empile-horizontal':
    case 'horizontal': return 'x'
    case 'standard': return 'y'
  }
}

/** Le total d'une barre horizontale simple, quand afficher une PART à côté de la valeur a
 *  un sens (D-8 : « part et valeur au bout de chaque barre »).
 *
 *  Trois conditions, et aucune n'est décorative :
 *   · une SEULE série — avec plusieurs, le dénominateur serait ambigu et la part inventerait
 *     un sens que la donnée ne porte pas ;
 *   · des valeurs ABSOLUES — sur des parts déjà servies, rediviser fabriquerait un second
 *     pourcentage (même défaut que la revue P2 sur les anneaux) ;
 *   · un total non nul.
 *
 *  Rend `undefined` dès qu'une condition manque : la barre porte alors sa valeur seule,
 *  ce qui est toujours vrai. */
export function totalPourPart(
  forme: FormeEchelles,
  datasets: readonly { data: readonly (number | null)[] }[],
  unitEstProportion: boolean,
): number | undefined {
  if (forme !== 'horizontal' || datasets.length !== 1 || unitEstProportion) return undefined
  const total = datasets[0].data.reduce<number>((s, v) => s + (v ?? 0), 0)
  return total > 0 ? total : undefined
}
